import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { marketplaceService } from '../../../_services/marketplace.service';
import {
  getMarketplaceHomeCache,
  setMarketplaceHomeCache,
} from '../../../_services/marketplaceCache';
import { hideSplashScreen } from '../../../_services/splashScreen';
import { hapticTick } from '../../../_utils/haptics';
import { getRecentSearches, addRecentSearch, removeRecentSearch, clearRecentSearches } from '../../../_utils/recentSearches';
import toast from '../../shared/Toast';
import { walletService } from '../../../_services/wallet.service';
import { normalizeLoyaltyCard } from '../../wallet/useWallet';
import { usePullToRefresh } from '../../notifications/usePullToRefresh';
import '../../common/PullToRefresh/PullToRefresh.css';
import DashboardSidebar from '../../clientDashboard/DashboardSidebar';
import BeuLoader from '../../common/BeuLoader';
import SalonImageCarousel from '../../common/SalonImageCarousel';
import LazyImage from '../../common/LazyImage';
import '../../clientDashboard/dashboard.css';
import './NewHomePage.css';

// Icons as SVG components
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const StarIcon = ({ filled = true, size = 16 }) => (
  <svg
    className="star-icon"
    viewBox="0 0 24 24"
    fill={filled ? '#000' : 'none'}
    stroke="#000"
    strokeWidth="1"
    width={size}
    height={size}
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

// Heart icon for favorites
const HeartIcon = ({ filled = false, size = 20 }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? "#e74c3c" : "none"}
    stroke={filled ? "#e74c3c" : "currentColor"}
    strokeWidth="2"
    className="heart-icon"
    width={size}
    height={size}
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

// Calendar icon for Recommended Stylists section
const CalendarTabIcon = ({ active = false, size = 24 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={active ? "#000" : "#999"} strokeWidth="2" width={size} height={size}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// User icon for Recommended Stylists section
const UserTabIcon = ({ active = false, size = 24 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={active ? "#000" : "#999"} strokeWidth="2" width={size} height={size}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// Location/Target icon for Recommended Stylists
const LocationTabIcon = ({ active = false, size = 20 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={active ? "#3BBFE5" : "#999"} strokeWidth="2" width={size} height={size}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="3" />
    <line x1="12" y1="2" x2="12" y2="4" />
    <line x1="12" y1="20" x2="12" y2="22" />
    <line x1="2" y1="12" x2="4" y2="12" />
    <line x1="20" y1="12" x2="22" y2="12" />
  </svg>
);

// Placeholder image component for salons without images
const PlaceholderImage = ({ className }) => (
  <div className={`${className} placeholder-image`}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
    <span>No Image</span>
  </div>
);

const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="spinner"></div>
  </div>
);

// Skeleton component for search results loading state
const SearchResultsSkeleton = () => (
  <div className="search-results-skeleton">
    <div className="skeleton-cards-grid">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="skeleton-salon-card">
          <div className="skeleton-card-image"></div>
          <div className="skeleton-card-info">
            <div className="skeleton-card-title"></div>
            <div className="skeleton-card-subtitle"></div>
            <div className="skeleton-card-meta"></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Skeleton renders the same layout shell as the real page so there's no shift on data arrival.
const MarketplaceSkeleton = () => {
  const renderFullCard = (key) => (
    <div key={key} className="favourite-card-full sk-card">
      <div className="sk-shimmer sk-card-image" />
    </div>
  );

  return (
    <div className="new-homepage logged-out marketplace-skeleton">
      <div className="homepage-content-new">
        <div className="homepage-main-content">
          <div className="homepage-search-card">
            {/* Logo corner — desktop only, matches real layout */}
            <div className="search-card-logo-corner">
              <div className="sk-shimmer sk-logo" />
            </div>
            {/* Auth corner — desktop only, matches real layout */}
            <div className="search-card-auth-corner">
              <div className="sk-shimmer sk-auth-login" />
              <div className="sk-shimmer sk-auth-signup" />
            </div>
            <div className="search-card-expanded">
              <div className="search-card-expanded-inner">
                <div className="sk-shimmer sk-search-bar" />
                <div className="homepage-category-tabs header-tabs sk-tabs-wrap">
                  <div className="sk-shimmer sk-tab" />
                  <div className="sk-shimmer sk-tab" />
                  <div className="sk-shimmer sk-tab" />
                  <div className="sk-shimmer sk-tab" />
                </div>
              </div>
            </div>
          </div>

          <section className="homepage-section">
            <div className="sk-shimmer sk-section-title" />
            <div className="book-again-scroll">
              <div className="book-again-card-new sk-book-card">
                <div className="book-again-left">
                  <div className="sk-shimmer sk-book-avatar" />
                </div>
                <div className="book-again-center sk-book-center">
                  <div className="sk-shimmer sk-line sk-line--title" />
                  <div className="sk-shimmer sk-line sk-line--subtitle" />
                  <div className="sk-shimmer sk-line sk-line--btn" />
                </div>
              </div>
            </div>
          </section>

          <section className="homepage-section">
            <div className="sk-shimmer sk-section-title" />
            <div className="favourites-list">
              {[0, 1, 2].map((i) => renderFullCard(`fav-${i}`))}
            </div>
          </section>

          <section className="homepage-section">
            <div className="sk-shimmer sk-section-title" />
            <div className="favourites-list">
              {[0, 1, 2].map((i) => renderFullCard(`rec-${i}`))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

// London fallback coordinates — used when the user's cached or actual
// location returns zero salons (no coverage in their area yet). Mirrors
// the default in marketplaceService.getCurrentLocation().
const LONDON_FALLBACK = { latitude: 51.5074, longitude: -0.1278 };

// Coarse test for "is this location basically London?" so we don't fall
// back to London when we're already there.
const isLondonFallback = (loc) =>
  Math.abs(loc.latitude - LONDON_FALLBACK.latitude) < 0.01 &&
  Math.abs(loc.longitude - LONDON_FALLBACK.longitude) < 0.01;

// True if the home payload contains at least one salon in any of the
// main visible sections. Used to decide whether a location is worth
// keeping on screen / persisting / caching.
const homeHasAnySalons = (homeData) =>
  (homeData?.recommended?.length > 0) ||
  (homeData?.recommendedNearYou?.length > 0) ||
  (homeData?.availableToday?.length > 0) ||
  (homeData?.featured?.length > 0) ||
  (homeData?.newToBeU?.length > 0);

// Category tabs for filtering
// Results per search page; the API's pagination gives us hasMore for the rest.
const SEARCH_PAGE_SIZE = 20;

const CATEGORY_TABS = [
  { id: 'all', label: 'All' },
  { id: 'salons', label: 'Salons' },
  { id: 'barbers', label: 'Barbers' },
  { id: 'beauty', label: 'Beauty' }
];

// Client-side category classifier. The home payload doesn't carry a business
// type - each item has service-category NAMES (categories[] +
// primaryCategory, e.g. "Gentlemen", "Hair Styling", "Waxing") - so tabs
// match on those. An item can sit in more than one tab (a hair salon that
// does gents cuts shows under Salons AND Barbers, which is what a browsing
// client wants). Items with no category data only appear under All.
// The lasting fix is a server-side category param on marketplace/home
// (backend wishlist); this classifier is the interim.
const CATEGORY_MATCHERS = {
  barbers: /gentlemen|barber|beard|shave|fade/i,
  salons: /hair|colou?r|blow.?dry|styling|braid|extension|keratin|treatment/i,
  beauty: /beauty|nail|wax|lash|brow|massage|facial|skin|spa|tan|make.?up|aesthet|thread|mani|pedi/i,
};

function itemMatchesCategory(item, categoryId) {
  if (categoryId === 'all') return true;
  const matcher = CATEGORY_MATCHERS[categoryId];
  if (!matcher) return true;
  const names = [item?.primaryCategory, ...(Array.isArray(item?.categories) ? item.categories : [])]
    .filter((n) => typeof n === 'string');
  return names.some((n) => matcher.test(n));
}

// Stylist filter tabs for Recommended Stylists section
const STYLIST_TABS = [
  { id: 'location', icon: LocationTabIcon },
  { id: 'calendar', icon: CalendarTabIcon },
  { id: 'user', icon: UserTabIcon }
];

const NewHomePage = ({ dispatch, salonList, user }) => {
  const history = useHistory();
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Data state
  const [recommendedSalons, setRecommendedSalons] = useState([]);
  const [newToBeuSalons, setNewToBeuSalons] = useState([]);
  const [featuredSalons, setFeaturedSalons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favoriteSalons, setFavoriteSalons] = useState([]);
  const [bookAgain, setBookAgain] = useState(null);
  // The signed-in client's most rewarding loyalty card (reward ready, or
  // closest to one) - rendered as a slim strip on the All feed.
  const [loyaltyNudge, setLoyaltyNudge] = useState(null);
  const [availableToday, setAvailableToday] = useState([]);
  const [recommendedNearYou, setRecommendedNearYou] = useState([]);
  
  // Discover more state
  const [discoverMoreSalons, setDiscoverMoreSalons] = useState([]);
  const [discoverMoreStylists, setDiscoverMoreStylists] = useState([]);
  const [isLoadingMoreSalons, setIsLoadingMoreSalons] = useState(false);
  const [isLoadingMoreStylists, setIsLoadingMoreStylists] = useState(false);
  const [hasMoreSalons, setHasMoreSalons] = useState(true);
  const [hasMoreStylists, setHasMoreStylists] = useState(true);
  
  // Filter state
  const [activeCategory, setActiveCategory] = useState('all');
  // Category swipe gesture: live drag offset (px) while a horizontal swipe is
  // in flight, and the direction of the last commit so the incoming feed can
  // slide in from the correct side.
  const [categoryDragX, setCategoryDragX] = useState(0);
  const [categorySlideDir, setCategorySlideDir] = useState(null); // 'left' | 'right' | null
  const categoryTouchRef = useRef({ x: 0, y: 0, axis: null, active: false });
  // Pull-to-refresh on the whole marketplace (window-scrolled page).
  const homeContentRef = useRef(null);
  // Sliding underline under the tabs, measured from real tab geometry.
  const categoryTabRefs = useRef({});
  const categoryTabsRowRef = useRef(null);
  const [tabIndicator, setTabIndicator] = useState(null);
  const [activeStylistTab, setActiveStylistTab] = useState('calendar');
  const [featuredIndex, setFeaturedIndex] = useState(0);
  
  // Search results state
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMoreResults, setIsLoadingMoreResults] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => getRecentSearches());
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchMoreObserverRef = useRef(null);
  const loadMoreRef = useRef(null);
  
  // Sticky header state
  const [isSearchCardSticky, setIsSearchCardSticky] = useState(false);
  const searchCardRef = useRef(null);
  const searchInputRef = useRef(null);
  const stickySentinelRef = useRef(null);

  // Smooth-scroll to top when the collapsed pill is tapped, then focus the
  // real search input once the expand animation has finished.
  const handleExpandFromPill = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      if (searchInputRef.current) searchInputRef.current.focus();
    }, 350);
  }, []);
  
  // Debounce ref for search
  const searchTimeoutRef = useRef(null);
  const searchAbortControllerRef = useRef(null);

  // Hide the main navigation bar on mount and fetch data
  useEffect(() => {
    document.body.classList.add('login-home-temp');
    document.body.classList.add('marketplace-page');
    document.body.classList.remove('nav_menu_open');
    document.body.classList.remove('no-topbar');
    
    initializeMarketplace();
    
    return () => {
      document.body.classList.remove('login-home-temp');
      document.body.classList.remove('marketplace-page');
    };
  }, []);

  // Apply a fetched homeData blob to local state. Kept separate from the
  // fetching code so the initial load, the silent post-geolocation refetch,
  // and cache hits can all share the same state-setting logic.
  //
  // Also hides the native splash screen the first time we render real data,
  // whether that comes from the in-memory cache (instant on re-entry) or a
  // fresh API response (after first cold-start fetch). No-op on web.
  const applyHomeData = useCallback((homeData) => {
    setRecommendedSalons(homeData.recommended || []);
    setNewToBeuSalons(homeData.newToBeU || []);
    setFeaturedSalons(homeData.featured || []);
    setCategories(homeData.categories || []);
    setFavoriteSalons(homeData.favorites || []);
    // Accept both shapes: today's API sends a single object; an array
    // renders as a scrollable row of recent places with no further change.
    const ba = homeData.bookAgain;
    setBookAgain(Array.isArray(ba) ? ba : (ba ? [ba] : null));
    setAvailableToday(homeData.availableToday || homeData.recommended?.slice(0, 4) || []);
    setRecommendedNearYou(homeData.recommendedNearYou || homeData.recommended?.slice(0, 4) || []);
    hideSplashScreen();
  }, []);

  // Initialize marketplace with user location.
  //
  // Previously this awaited getCurrentLocation() (up to 5s on native, plus
  // the permission prompt) BEFORE calling getMarketplaceHome(). On a Capacitor
  // cold start that meant the marketplace sat on a skeleton for seconds.
  //
  // New flow:
  //   1. Synchronously read the cached location (or London default) and
  //      kick off getMarketplaceHome() immediately.
  //   2. In parallel, run getCurrentLocation() (same implementation, still
  //      5s timeout fallback to London).
  //   3. When real GPS resolves, if the coordinates differ materially from
  //      what we rendered with, silently refetch and swap the data in.
  const initializeMarketplace = async () => {
    try {
      setError(null);

      // (1) Immediately use the best location we can produce without blocking.
      const initialLocation = marketplaceService.getCachedLocation();
      setLocation(initialLocation);

      // (1b) Stale-while-revalidate: if we have a recent homeData payload in
      // memory (e.g. user flicked over to Bookings/Account and came back),
      // render it instantly and skip the skeleton entirely. We still re-fetch
      // in the background so the data can't go stale during the session.
      const cachedHome = getMarketplaceHomeCache(initialLocation);
      if (cachedHome) {
        applyHomeData(cachedHome);
        setLoading(false);
      } else {
        setLoading(true);
      }

      // Track which location actually ended up serving data on screen. We use
      // this below to decide whether it's safe to persist `accurateLocation`
      // to localStorage for next cold start — persisting a location that
      // yielded empty would poison future boots.
      let servingLocation = initialLocation;

      // (2a) Fire the first data fetch now — don't wait for geolocation.
      // If the fetch returns empty (and we weren't already on London), clear
      // the poisoned cache and refetch once with London so users who sit
      // outside our coverage get real content instead of empty placeholders.
      const firstFetch = marketplaceService
        .getMarketplaceHome({
          latitude: initialLocation.latitude,
          longitude: initialLocation.longitude,
          maxDistance: 50000
        })
        .then(async (homeData) => {
          if (!homeHasAnySalons(homeData) && !isLondonFallback(initialLocation)) {
            console.log('[Marketplace] Initial location returned no salons; falling back to London');
            marketplaceService.clearCachedLocation();
            try {
              const fallback = await marketplaceService.getMarketplaceHome({
                latitude: LONDON_FALLBACK.latitude,
                longitude: LONDON_FALLBACK.longitude,
                maxDistance: 50000
              });
              setLocation(LONDON_FALLBACK);
              servingLocation = LONDON_FALLBACK;
              setMarketplaceHomeCache(fallback, LONDON_FALLBACK);
              applyHomeData(fallback);
              return;
            } catch (fallbackErr) {
              // London fetch also failed. Fall through and apply whatever
              // the empty-but-successful first response contained — at least
              // the loader clears.
              console.warn('[Marketplace] London fallback fetch failed:', fallbackErr);
            }
          }
          setMarketplaceHomeCache(homeData, initialLocation);
          applyHomeData(homeData);
        })
        .catch((err) => {
          console.error('Error initializing marketplace:', err);
          // Don't block the entire page on API failure - show empty state instead
          // (but only if we didn't already paint from cache - otherwise a
          // background refetch failure shouldn't blow away good cached data).
          if (!cachedHome) {
            setRecommendedSalons([]);
            setNewToBeuSalons([]);
            setFeaturedSalons([]);
            setCategories([]);
            setFavoriteSalons([]);
            setAvailableToday([]);
            setRecommendedNearYou([]);
          }
        })
        .finally(() => {
          setLoading(false);
        });

      // (2b) In parallel, resolve real geolocation. Note: getCurrentLocation
      // no longer auto-persists its result — we do that below, conditionally
      // on the coords actually yielding data.
      const accurateLocation = await marketplaceService.getCurrentLocation();

      // (3) If geolocation gave us a materially different location (>~5km),
      // refetch silently so distance-sorted results match where the user is.
      // Cheap lat/lng delta to avoid a haversine dependency.
      const materiallyDifferent =
        Math.abs(accurateLocation.latitude - servingLocation.latitude) > 0.05 ||
        Math.abs(accurateLocation.longitude - servingLocation.longitude) > 0.05;

      if (materiallyDifferent) {
        setLocation(accurateLocation);
        // Wait for the initial fetch to settle before swapping data in so
        // we don't race it and flash stale content back in.
        await firstFetch;
        try {
          const homeData = await marketplaceService.getMarketplaceHome({
            latitude: accurateLocation.latitude,
            longitude: accurateLocation.longitude,
            maxDistance: 50000
          });

          if (homeHasAnySalons(homeData)) {
            setMarketplaceHomeCache(homeData, accurateLocation);
            applyHomeData(homeData);
            servingLocation = accurateLocation;
          } else {
            console.log('[Marketplace] Accurate location returned no salons; keeping current payload on screen');
            // Snap displayed location back to whatever actually served the
            // data so distance labels stay consistent.
            setLocation(servingLocation);
          }
        } catch (err) {
          // Keep whatever the first fetch produced; silent refetch is best-effort.
          console.warn('[Marketplace] Silent refetch with accurate location failed:', err);
        }
      } else if (
        accurateLocation.latitude !== servingLocation.latitude ||
        accurateLocation.longitude !== servingLocation.longitude
      ) {
        // Same-ish location but different lat/lng — keep state in sync for
        // downstream handlers (search, discover-more) without refetching.
        setLocation(accurateLocation);
      }

      // (4) Wait for firstFetch to settle so `servingLocation` reflects any
      // London fallback the firstFetch.then() may have applied. Without this
      // await we could persist a stale initialLocation while the fallback is
      // still in flight, re-poisoning localStorage.
      await firstFetch;

      // Persist the location that ACTUALLY served data, never a location
      // that came back empty. Skip persisting the London default because
      // that's already our fallback when no cache exists.
      if (!isLondonFallback(servingLocation)) {
        marketplaceService.persistLocation(servingLocation);
      } else {
        marketplaceService.clearCachedLocation();
      }
    } catch (err) {
      // Top-level safety net — individual fetches already swallow their own errors.
      console.error('Error initializing marketplace:', err);
      setLoading(false);
    }
  };

  // Handle salon click - prefer slug URL for SEO
  const handleSalonClick = useCallback((salonId, slug) => {
    history.push(slug ? `/book/${slug}` : `/salon/${salonId}`);
  }, [history]);

  const handleStylistClick = useCallback((salonId, hairdresserId, serviceIds = null, salonSlug = null) => {
    const base = salonSlug ? `/book/${salonSlug}` : `/salon/${salonId}`;
    if (salonId && hairdresserId) {
      let url = `${base}/professional/${hairdresserId}`;
      if (serviceIds && Array.isArray(serviceIds) && serviceIds.length > 0) {
        url += `?rebookServiceIds=${serviceIds.join(',')}`;
      }
      history.push(url);
    } else if (salonId) {
      history.push(base);
    }
  }, [history]);

  // Run a search for `query`. Debouncing happens in the caller; this owns
  // cancellation, so a superseded request can never land after a newer one
  // and repaint stale results (a fast typist could previously race them).
  const runSearch = useCallback(async (query) => {
    if (!location) return;
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.cancel('superseded');
    }
    const source = marketplaceService.searchSalons.cancelSource();
    searchAbortControllerRef.current = source;

    setIsSearching(true);
    try {
      const results = await marketplaceService.searchSalons({
        latitude: location.latitude,
        longitude: location.longitude,
        searchQuery: query,
        category: activeCategory !== 'all' ? activeCategory : undefined,
        limit: SEARCH_PAGE_SIZE,
        cancelToken: source.token
      });
      setSearchResults(results);
      setIsSearching(false);
    } catch (err) {
      // A cancelled request means a newer one is already in flight - leave
      // the searching state alone so the spinner doesn't flicker off.
      if (marketplaceService.searchSalons.isCancelled(err)) return;
      console.error('Search error:', err);
      setSearchResults({ salons: [], pagination: { total: 0, hasMore: false } });
      setIsSearching(false);
    }
  }, [location, activeCategory]);

  // Handle search with debouncing
  const handleSearch = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      // Abandoning the query also abandons any request it started.
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.cancel('cleared');
        searchAbortControllerRef.current = null;
      }
      setSearchResults(null);
      setIsSearching(false);
      return;
    }

    if (!location) return;

    // Show searching state immediately
    setIsSearching(true);

    // Debounce the actual API call by 300ms
    searchTimeoutRef.current = setTimeout(() => runSearch(query), 300);
  }, [location, runSearch]);

  // A recent search records INTENT, not keystrokes: saving on every debounced
  // fetch stored each prefix the user typed through ("ba", "bar", "barb"...).
  // Committing happens when they act on the results - opening one, or
  // pressing Enter - which is the term they'd actually want to repeat.
  const commitRecentSearch = useCallback((term) => {
    const trimmed = (term || '').trim();
    if (trimmed) setRecentSearches(addRecentSearch(trimmed));
  }, []);

  const handleSearchResultClick = useCallback((salonId, slug) => {
    commitRecentSearch(searchQuery);
    handleSalonClick(salonId, slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitRecentSearch, searchQuery]);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    const value = e.target.value;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    commitRecentSearch(value);
    if (value.trim()) runSearch(value);
    e.target.blur();
  }, [commitRecentSearch, runSearch]);

  // Tapping a recent term fills the box and searches straight away - no
  // debounce, the intent is explicit.
  const handleRecentSearch = useCallback((term) => {
    hapticTick();
    setRecentSearches(addRecentSearch(term)); // promote to newest
    setSearchQuery(term);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    runSearch(term);
  }, [runSearch]);

  // Append the next page of results. Guarded so overlapping triggers (the
  // sentinel can fire repeatedly while it stays on screen) can't double-load.
  const loadMoreSearchResults = useCallback(async () => {
    if (!location || isLoadingMoreResults) return;
    const current = searchResults;
    if (!current?.pagination?.hasMore) return;

    setIsLoadingMoreResults(true);
    try {
      const next = await marketplaceService.searchSalons({
        latitude: location.latitude,
        longitude: location.longitude,
        searchQuery: searchQuery,
        category: activeCategory !== 'all' ? activeCategory : undefined,
        limit: SEARCH_PAGE_SIZE,
        skip: (current.salons || []).length
      });
      setSearchResults((prev) => ({
        ...next,
        salons: [...((prev && prev.salons) || []), ...((next && next.salons) || [])]
      }));
    } catch (err) {
      if (!marketplaceService.searchSalons.isCancelled(err)) {
        console.error('Load more search results failed:', err);
      }
    } finally {
      setIsLoadingMoreResults(false);
    }
  }, [location, searchQuery, activeCategory, searchResults, isLoadingMoreResults]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Toggle the visual "shrunk" morph when a sentinel above the card scrolls
  // out of view. IntersectionObserver fires only on boundary crossings so the
  // morph never oscillates (a scroll listener would re-fire as the morph
  // reshapes layout, causing the class to flap between states).
  //
  // Depends on `loading` so the effect re-runs once the real content renders —
  // during the loading state the sentinel isn't in the DOM yet.
  useEffect(() => {
    if (loading) return undefined;
    const el = stickySentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    // rootMargin extends the viewport upward by 40px so the sentinel (at page
    // y=0) stays "intersecting" until the user has scrolled past 40px. Fires
    // the morph when they show intent to scroll, not on every pixel.
    const observer = new IntersectionObserver(
      ([entry]) => setIsSearchCardSticky(!entry.isIntersecting),
      { threshold: 0, rootMargin: '40px 0px 0px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading]);

  // Mirror the sticky state onto <body> so .homepage-main-content's padding-top
  // can shrink in sync with the card's morph (card is position: fixed, so the
  // main content's top padding is what reserves its footprint in the flow).
  useEffect(() => {
    if (isSearchCardSticky) {
      document.body.classList.add('is-marketplace-sticky');
    } else {
      document.body.classList.remove('is-marketplace-sticky');
    }
    return () => document.body.classList.remove('is-marketplace-sticky');
  }, [isSearchCardSticky]);

  // Handle category selection. Tabs now FILTER the curated feed client-side
  // (see itemMatchesCategory) instead of replacing it with a search-results
  // grid. The search endpoint is only involved when the user has actually
  // typed a query - then the tab acts as a filter on that search.
  const handleCategorySelect = useCallback(async (categoryId) => {
    setActiveCategory(categoryId);
    hapticTick();

    if (searchQuery) {
      if (!location) return;

      setIsSearching(true);
      try {
        const results = await marketplaceService.searchSalons({
          latitude: location.latitude,
          longitude: location.longitude,
          searchQuery,
          category: categoryId !== 'all' ? categoryId : undefined,
          limit: 20
        });
        setSearchResults(results);
      } catch (err) {
        console.error('Filter search error:', err);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults(null);
    }
  }, [location, searchQuery]);

  // Keep the tabs' underline glued to the active tab; it also rides along
  // with a live category swipe (see render).
  useEffect(() => {
    const measure = () => {
      const row = categoryTabsRowRef.current;
      const el = categoryTabRefs.current[activeCategory];
      if (!row || !el) return;
      const rowRect = row.getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      setTabIndicator({ x: rect.left - rowRect.left, width: rect.width });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activeCategory, loading]);

  // Pull-to-refresh: silent refetch of the home payload, overwriting the
  // 5-minute cache. Current data stays on screen if the refetch fails.
  // `loading` is deliberately in the deps: the gesture's target element only
  // exists once the skeleton has gone, and a changed callback identity is
  // what makes the hook re-attach its listeners at that point.
  const refreshHome = useCallback(async () => {
    const loc = location || marketplaceService.getCachedLocation();
    try {
      const homeData = await marketplaceService.getMarketplaceHome({
        latitude: loc.latitude,
        longitude: loc.longitude,
        maxDistance: 50000
      });
      setMarketplaceHomeCache(homeData, loc);
      applyHomeData(homeData);
    } catch (err) {
      console.warn('Pull-to-refresh fetch failed:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, applyHomeData, loading]);

  const homePtr = usePullToRefresh(homeContentRef, refreshHome, { windowScroll: true });

  // Loyalty nudge: one fetch on mount for signed-in clients. Picks the card
  // with a claimable reward, else the one closest to its reward - the
  // strongest reason to rebook that the home screen can show.
  useEffect(() => {
    if (!localStorage.getItem('accessToken')) return undefined;
    let cancelled = false;
    walletService.getMyLoyaltyCards().then((cards) => {
      if (cancelled || !Array.isArray(cards) || cards.length === 0) return;
      const items = cards.map(normalizeLoyaltyCard);
      const progressRatio = (item) => {
        const scheme = item.scheme || {};
        const state = item.state || {};
        if ((state.rewardsAvailable || 0) > 0) return 1;
        return scheme.mode === 'points'
          ? (state.balance || 0) / (scheme.pointsTarget || 500)
          : (state.filled || 0) / (scheme.stampsRequired || 6);
      };
      const best = items
        .filter((i) => progressRatio(i) > 0) // nothing to say about untouched cards
        .sort((a, b) => progressRatio(b) - progressRatio(a))[0];
      if (best) setLoyaltyNudge(best);
    });
    return () => { cancelled = true; };
  }, []);

  // Keep the newest loader in a ref so the sentinel's callback ref below can
  // stay identity-stable (it must not re-attach on every results change).
  useEffect(() => {
    loadMoreRef.current = loadMoreSearchResults;
  }, [loadMoreSearchResults]);

  // Infinite results: a sentinel below the last card pulls the next page in
  // as it nears the viewport, so the list just keeps going instead of
  // stopping silently at 20 with 54 matches found.
  //
  // A CALLBACK ref, not useRef + useEffect: the sentinel mounts and unmounts
  // with the results list, and an effect keyed on anything else can easily
  // run at a moment when the node isn't there. This observes exactly when
  // React hands us the node.
  const attachSearchSentinel = useCallback((node) => {
    if (searchMoreObserverRef.current) {
      searchMoreObserverRef.current.disconnect();
      searchMoreObserverRef.current = null;
    }
    if (!node || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && loadMoreRef.current) {
          loadMoreRef.current();
        }
      },
      { rootMargin: '300px' }
    );
    io.observe(node);
    searchMoreObserverRef.current = io;
  }, []);

  // Clear the feed's enter animation class once it has played.
  useEffect(() => {
    if (!categorySlideDir) return undefined;
    const t = setTimeout(() => setCategorySlideDir(null), 260);
    return () => clearTimeout(t);
  }, [categorySlideDir]);

  // Auto-load initial discover more items when main data has loaded
  useEffect(() => {
    // Wait for location and for main sections to have loaded (not loading anymore)
    if (!location || loading) return;
    
    // Only load once - check if we've already attempted to load
    if (discoverMoreSalons.length > 0 || isLoadingMoreSalons) return;
    
    // Load initial discover more salons
    const loadInitialDiscoverSalons = async () => {
      setIsLoadingMoreSalons(true);
      try {
        // Collect all salon IDs from other sections to exclude
        const excludeIds = [];
        recommendedSalons.forEach(s => s._id && excludeIds.push(s._id));
        featuredSalons.forEach(s => s._id && excludeIds.push(s._id));
        recommendedNearYou.forEach(s => s._id && excludeIds.push(s._id));
        favoriteSalons.forEach(s => s._id && excludeIds.push(s._id));
        availableToday.forEach(s => s._id && excludeIds.push(s._id));
        
        console.log('Loading discover more salons, excluding:', excludeIds.length, 'salons');
        
        const results = await marketplaceService.getDiscoverMoreSalons({
          latitude: location.latitude,
          longitude: location.longitude,
          limit: 4,
          skip: 0,
          excludeIds
        });
        
        const newSalons = results.salons || results || [];
        if (newSalons.length === 0) {
          setHasMoreSalons(false);
        } else {
          setDiscoverMoreSalons(newSalons);
        }
      } catch (err) {
        console.error('Error loading initial discover salons:', err);
        setHasMoreSalons(false);
      } finally {
        setIsLoadingMoreSalons(false);
      }
    };

    // Load initial discover more stylists
    const loadInitialDiscoverStylists = async () => {
      if (discoverMoreStylists.length > 0 || isLoadingMoreStylists) return;
      
      setIsLoadingMoreStylists(true);
      try {
        const results = await marketplaceService.getDiscoverMoreStylists({
          latitude: location.latitude,
          longitude: location.longitude,
          limit: 4,
          skip: 0,
          excludeIds: []
        });
        
        const newStylists = results.salons || results || [];
        if (newStylists.length === 0) {
          setHasMoreStylists(false);
        } else {
          setDiscoverMoreStylists(newStylists);
        }
      } catch (err) {
        console.error('Error loading initial discover stylists:', err);
        setHasMoreStylists(false);
      } finally {
        setIsLoadingMoreStylists(false);
      }
    };

    // Load both sections
    loadInitialDiscoverSalons();
    loadInitialDiscoverStylists();
  }, [location, loading, recommendedSalons, featuredSalons, recommendedNearYou, favoriteSalons, availableToday]);

  // Handle menu toggle
  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  // Get all currently displayed salon IDs to exclude from discover more
  const getDisplayedSalonIds = useCallback(() => {
    const ids = new Set();
    recommendedSalons.forEach(s => ids.add(s._id));
    featuredSalons.forEach(s => ids.add(s._id));
    recommendedNearYou.forEach(s => ids.add(s._id));
    favoriteSalons.forEach(s => ids.add(s._id));
    availableToday.forEach(s => ids.add(s._id));
    discoverMoreSalons.forEach(s => ids.add(s._id));
    return Array.from(ids);
  }, [recommendedSalons, featuredSalons, recommendedNearYou, favoriteSalons, availableToday, discoverMoreSalons]);

  // Get all currently displayed stylist IDs to exclude from discover more
  const getDisplayedStylistIds = useCallback(() => {
    const ids = new Set();
    recommendedSalons.forEach(s => s.stylistId && ids.add(s.stylistId));
    discoverMoreStylists.forEach(s => s.stylistId && ids.add(s.stylistId));
    return Array.from(ids);
  }, [recommendedSalons, discoverMoreStylists]);

  // Load more salons
  const handleLoadMoreSalons = useCallback(async () => {
    if (!location || isLoadingMoreSalons || !hasMoreSalons) return;
    
    setIsLoadingMoreSalons(true);
    try {
      const excludeIds = getDisplayedSalonIds();
      const results = await marketplaceService.getDiscoverMoreSalons({
        latitude: location.latitude,
        longitude: location.longitude,
        limit: 4,
        skip: discoverMoreSalons.length,
        excludeIds
      });
      
      const newSalons = results.salons || results || [];
      if (newSalons.length === 0) {
        setHasMoreSalons(false);
      } else {
        setDiscoverMoreSalons(prev => [...prev, ...newSalons]);
      }
    } catch (err) {
      console.error('Error loading more salons:', err);
    } finally {
      setIsLoadingMoreSalons(false);
    }
  }, [location, isLoadingMoreSalons, hasMoreSalons, discoverMoreSalons.length, getDisplayedSalonIds]);

  // Load more stylists
  const handleLoadMoreStylists = useCallback(async () => {
    if (!location || isLoadingMoreStylists || !hasMoreStylists) return;
    
    setIsLoadingMoreStylists(true);
    try {
      const excludeIds = getDisplayedStylistIds();
      const results = await marketplaceService.getDiscoverMoreStylists({
        latitude: location.latitude,
        longitude: location.longitude,
        limit: 4,
        skip: discoverMoreStylists.length,
        excludeIds
      });
      
      const newStylists = results.salons || results || [];
      if (newStylists.length === 0) {
        setHasMoreStylists(false);
      } else {
        setDiscoverMoreStylists(prev => [...prev, ...newStylists]);
      }
    } catch (err) {
      console.error('Error loading more stylists:', err);
    } finally {
      setIsLoadingMoreStylists(false);
    }
  }, [location, isLoadingMoreStylists, hasMoreStylists, discoverMoreStylists.length, getDisplayedStylistIds]);

  // Check if user is logged in - use accessToken for consistency with PrivateRoute
  const hasAccessToken = !!localStorage.getItem('accessToken');
  const isLoggedIn = hasAccessToken && user?.userInfo?.name;

  // Loading state - show skeleton only (no popover)
  if (loading) {
    return <MarketplaceSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="new-homepage">
        <div className="homepage-content">
          <div className="error-container">
            <p className="error-text">{error}</p>
            <button className="retry-button" onClick={initializeMarketplace}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Determine what to display
  const displaySalons = searchResults?.salons || null;
  const showSearchResults = displaySalons !== null;

  // -- Category filtering + swipe gesture (plain consts: we're past the
  //    early returns, so no hooks below this line) --

  const filterByActiveCategory = (items) =>
    (items || []).filter((i) => itemMatchesCategory(i, activeCategory));

  const fFavourites = filterByActiveCategory(favoriteSalons);
  const fRecommended = filterByActiveCategory(recommendedSalons);
  const fFeatured = filterByActiveCategory(featuredSalons);
  const fNearYou = filterByActiveCategory(
    recommendedNearYou.length > 0 ? recommendedNearYou : recommendedSalons
  );
  const fAvailableToday = filterByActiveCategory(
    availableToday.length > 0 ? availableToday : recommendedSalons
  );
  const fNewToBeu = filterByActiveCategory(newToBeuSalons);
  const fDiscoverSalons = filterByActiveCategory(discoverMoreSalons);
  const fDiscoverStylists = filterByActiveCategory(discoverMoreStylists);

  // One line of copy for the loyalty strip. British English, warm, precise.
  const loyaltyNudgeText = (item) => {
    const scheme = item.scheme || {};
    const state = item.state || {};
    const salonName = item.salon?.name || 'your salon';
    if ((state.rewardsAvailable || 0) > 0) {
      return `Your reward is ready to claim at ${salonName}`;
    }
    if (scheme.mode === 'points') {
      const needed = Math.max(0, (scheme.pointsTarget || 500) - (state.balance || 0));
      return `${needed.toLocaleString()} points from your reward at ${salonName}`;
    }
    const remaining = Math.max(0, (scheme.stampsRequired || 6) - (state.filled || 0));
    return `${remaining} ${remaining === 1 ? 'stamp' : 'stamps'} from your reward at ${salonName}`;
  };

  // Un-favourite with optimistic UI: the card leaves immediately (that's
  // the native expectation), and reappears with a toast only if the API
  // rejects. Salon vs stylist routes to the matching endpoint.
  const handleUnfavourite = (favorite) => {
    hapticTick();
    const isStylist = favorite.type === 'stylist';
    const previous = favoriteSalons;
    setFavoriteSalons(favoriteSalons.filter((f) => f._id !== favorite._id));
    const call = isStylist
      ? marketplaceService.toggleFavoriteStylist({ stylistId: favorite.userId || favorite.stylistId || favorite._id, isFavorite: false })
      : marketplaceService.toggleFavoriteSalon({ salonId: favorite._id, isFavorite: false });
    call.catch(() => {
      setFavoriteSalons(previous);
      toast.error('Could not update favourites. Please try again.');
    });
  };

  const activeCategoryIndex = CATEGORY_TABS.findIndex((t) => t.id === activeCategory);

  const commitCategorySwipe = (dir) => {
    const nextIndex = activeCategoryIndex + (dir === 'left' ? 1 : -1);
    if (nextIndex < 0 || nextIndex >= CATEGORY_TABS.length) return;
    setCategorySlideDir(dir);
    handleCategorySelect(CATEGORY_TABS[nextIndex].id);
  };

  // Touch-only horizontal swipe between category feeds. Axis-locked: the
  // first ~8px decide whether this gesture is ours (horizontal) or the
  // page's (vertical scroll), so scrolling never fights the swipe.
  //
  // Rows that scroll horizontally themselves (Book Again, Recommended
  // Stylists, Near You, Discover More...) own any gesture that starts
  // inside them - otherwise dragging through a card row would scroll the
  // row AND flip the tab. Walk up from the touch target looking for a
  // scrollable-x ancestor; found one = this gesture is not ours.
  const startedInsideHorizontalScroller = (target, boundary) => {
    let el = target;
    while (el && el !== boundary) {
      if (el.scrollWidth > el.clientWidth + 1) {
        const overflowX = getComputedStyle(el).overflowX;
        if (overflowX === 'auto' || overflowX === 'scroll') return true;
      }
      el = el.parentElement;
    }
    return false;
  };

  const handleFeedTouchStart = (e) => {
    if (startedInsideHorizontalScroller(e.target, e.currentTarget)) {
      categoryTouchRef.current.active = false;
      return;
    }
    const t = e.touches[0];
    categoryTouchRef.current = { x: t.clientX, y: t.clientY, axis: null, active: true };
  };

  const handleFeedTouchMove = (e) => {
    const ref = categoryTouchRef.current;
    if (!ref.active) return;
    const t = e.touches[0];
    const dx = t.clientX - ref.x;
    const dy = t.clientY - ref.y;
    if (!ref.axis) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      ref.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (ref.axis !== 'x') return;
    // Damped follow; extra resistance when there's no tab in that direction.
    const atEdge =
      (dx > 0 && activeCategoryIndex === 0) ||
      (dx < 0 && activeCategoryIndex === CATEGORY_TABS.length - 1);
    setCategoryDragX(dx * (atEdge ? 0.12 : 0.35));
  };

  const handleFeedTouchEnd = () => {
    const ref = categoryTouchRef.current;
    if (!ref.active) return;
    ref.active = false;
    const drag = categoryDragX;
    setCategoryDragX(0);
    if (ref.axis !== 'x') return;
    const COMMIT = 24; // damped px, roughly 70px of finger travel
    if (drag <= -COMMIT) commitCategorySwipe('left');
    else if (drag >= COMMIT) commitCategorySwipe('right');
  };

  // The underline rides opposite to the feed drag (finger left = next tab
  // right), scaled down so it hints rather than mirrors.
  const indicatorDragOffset = -categoryDragX * 0.6;

  // Label shown in the collapsed pill. Defaults to "Search" when no filter is active.
  const activeCategoryLabel =
    CATEGORY_TABS.find((t) => t.id === activeCategory)?.label === 'All' || !activeCategory
      ? 'Search'
      : CATEGORY_TABS.find((t) => t.id === activeCategory)?.label || 'Search';

  // Render the main content
  const renderMainContent = () => (
    <>
    {/* Sentinel at the top of the page — IntersectionObserver fires the morph
        when this crosses the viewport edge. Placed outside main-content so its
        document y is 0 (main-content has top padding that reserves the fixed card). */}
    <div ref={stickySentinelRef} className="search-card-sentinel" aria-hidden="true" />

    <div className="homepage-main-content" ref={homeContentRef}>
      {/* Pull-to-refresh indicator: appears under the header while pulling,
          spins while the refetch runs. */}
      {(homePtr.distance > 0 || homePtr.refreshing) && (
        <div
          className={`ptr-indicator${homePtr.refreshing ? ' spinning' : ''}`}
          style={{
            opacity: Math.min(1, homePtr.progress * 1.2),
            transform: `translateX(-50%) translateY(${Math.min(homePtr.distance * 0.6, 70)}px) rotate(${homePtr.progress * 270}deg)`,
          }}
          aria-hidden
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="18" height="18">
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <polyline points="21 3 21 9 15 9" />
          </svg>
        </div>
      )}
      <div
        ref={searchCardRef}
        className={`homepage-search-card ${isSearchCardSticky ? 'is-sticky' : ''}`}
      >
        {/* Logo corner — desktop-only; persists in both expanded and collapsed states */}
        <div className="search-card-logo-corner">
          <img
            src={require('../../../assets/images/beu-logo.png')}
            alt="Beu"
            className="marketplace-logo"
          />
        </div>

        {/* Auth corner — persists in both states on desktop logged-out */}
        {!isLoggedIn && (
          <div className="search-card-auth-corner">
            <button className="marketplace-login-btn" onClick={() => history.push('/login')}>
              Log in
            </button>
            <button className="marketplace-signup-btn" onClick={() => history.push('/signup')}>
              Sign up
            </button>
          </div>
        )}

        {/* Expanded content — collapses to 0 via grid-template-rows trick when .is-sticky */}
        <div className="search-card-expanded">
          <div className="search-card-expanded-inner">
            <div className="homepage-search-bar">
              <SearchIcon />
              <input
                ref={searchInputRef}
                type="text"
                className="homepage-search-input"
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearch}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => setIsSearchFocused(true)}
                // Delayed so a tap on a recent term lands before the panel
                // unmounts (blur fires first on touch).
                onBlur={() => setTimeout(() => setIsSearchFocused(false), 150)}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="homepage-search-clear"
                  aria-label="Clear search"
                  onClick={() => handleSearch({ target: { value: '' } })}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="16" height="16">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Recent searches: only while focused with an empty box, so it
                never covers results the user is reading. */}
            {isSearchFocused && !searchQuery && recentSearches.length > 0 && (
              <div className="homepage-recents">
                <div className="homepage-recents-head">
                  <span>Recent</span>
                  <button
                    type="button"
                    className="homepage-recents-clear"
                    onClick={() => setRecentSearches(clearRecentSearches())}
                  >
                    Clear
                  </button>
                </div>
                <div className="homepage-recents-list">
                  {recentSearches.map((term) => (
                    <span key={term} className="homepage-recent-chip">
                      <button
                        type="button"
                        className="homepage-recent-term"
                        onClick={() => handleRecentSearch(term)}
                      >
                        {term}
                      </button>
                      <button
                        type="button"
                        className="homepage-recent-remove"
                        aria-label={`Remove ${term} from recent searches`}
                        onClick={() => setRecentSearches(removeRecentSearch(term))}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="11" height="11">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="homepage-category-tabs header-tabs" ref={categoryTabsRowRef}>
              {tabIndicator && (
                <span
                  className="category-tab-indicator"
                  style={{
                    transform: `translateX(${tabIndicator.x + indicatorDragOffset}px)`,
                    width: `${tabIndicator.width}px`,
                    // While the finger is down the underline must track it
                    // 1:1; the eased travel is for tap/commit only.
                    transition: categoryDragX !== 0 ? 'none' : undefined,
                  }}
                  aria-hidden
                />
              )}
              {CATEGORY_TABS.map((tab) => (
                <button
                  key={tab.id}
                  ref={(el) => { categoryTabRefs.current[tab.id] = el; }}
                  className={`homepage-category-tab ${activeCategory === tab.id ? 'active' : ''}`}
                  onClick={() => handleCategorySelect(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Compact pill — fades in when collapsed; tap re-expands header + focuses input */}
        <button
          type="button"
          className="search-card-pill"
          onClick={handleExpandFromPill}
          aria-label="Open search"
          tabIndex={isSearchCardSticky ? 0 : -1}
        >
          <SearchIcon />
          <span>
            {searchQuery
              ? `${activeCategoryLabel} · ${searchQuery}`
              : activeCategoryLabel}
          </span>
        </button>
      </div>

      {/* Search Results or Default Content */}
      {showSearchResults || isSearching ? (
        <section className="homepage-section">
          <h2 className="homepage-section-title">
            {isSearching ? 'Searching…' : 'Search Results'}
            {/* Show what's loaded against the total, so "20 of 54" reads as
                more-to-come rather than looking like the whole answer. */}
            {!isSearching && searchResults?.pagination?.total > 0 && (
              displaySalons && displaySalons.length < searchResults.pagination.total
                ? ` (${displaySalons.length} of ${searchResults.pagination.total})`
                : ` (${searchResults.pagination.total})`
            )}
          </h2>
          {isSearching ? (
            <SearchResultsSkeleton />
          ) : displaySalons && displaySalons.length > 0 ? (
            <div className="homepage-salon-grid">
              {displaySalons.map((salon) => (
                <div
                  key={salon._id}
                  className="homepage-salon-card"
                >
                  <div className="homepage-salon-image-container">
                    <SalonImageCarousel
                      mainImage={salon.image}
                      galleryImages={salon.galleryImages}
                      altText={salon.salonName}
                      primaryCategory={salon.primaryCategory}
                      businessType={salon.businessType}
                      onClick={() => handleSearchResultClick(salon._id, salon.slug)}
                      overlay={
                        salon.reviewCount > 0 && (
                          <div className="homepage-salon-rating">
                            <StarIcon filled={true} size={9} />
                            <span>{salon.averageRating?.toFixed(1)}</span>
                            <span>({salon.reviewCount})</span>
                          </div>
                        )
                      }
                    />
                  </div>
                  <div className="homepage-salon-info" onClick={() => handleSearchResultClick(salon._id, salon.slug)}>
                    <h3 className="homepage-salon-name">
                      {salon.salonName}
                      {salon.isClaimed === false && <span className="unclaimed-badge">Unclaimed</span>}
                    </h3>
                    <p className="homepage-salon-location">
                      {salon.formattedAddress || salon.administrativeAreaLevel2}
                    </p>
                    <div className="homepage-salon-meta">
                      <span className="homepage-salon-distance">{salon.distanceInMiles ? `${salon.distanceInMiles} mi` : ''}</span>
                      <span className="homepage-salon-type">{salon.primaryCategory || 'Hair Salon'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="search-empty">
              <div className="search-empty-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" width="28" height="28">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.3-4.3" />
                </svg>
              </div>
              <h3 className="search-empty-title">No matches for “{searchQuery}”</h3>
              <p className="search-empty-text">
                {activeCategory !== 'all'
                  ? 'Try a different spelling, or search across all categories.'
                  : 'Try a different spelling, or search for a service like “balayage”.'}
              </p>
              {activeCategory !== 'all' && (
                <button type="button" className="search-empty-cta" onClick={() => handleCategorySelect('all')}>
                  Search all categories
                </button>
              )}
            </div>
          )}

          {/* Infinite results: sentinel + status. Only rendered when there's
              more to fetch, so it can't spin forever at the end of the list. */}
          {!isSearching && displaySalons && displaySalons.length > 0 && (
            <div className="search-more">
              {searchResults?.pagination?.hasMore ? (
                <>
                  <div ref={attachSearchSentinel} className="search-more-sentinel" aria-hidden />
                  {isLoadingMoreResults && <div className="search-more-spinner" aria-label="Loading more results" />}
                </>
              ) : (
                displaySalons.length > SEARCH_PAGE_SIZE && (
                  <p className="search-more-end">That’s everything</p>
                )
              )}
            </div>
          )}
        </section>
      ) : (
        <div
          className={`category-feed${categorySlideDir ? ` category-feed--enter-${categorySlideDir}` : ''}`}
          style={categoryDragX !== 0 ? { transform: `translateX(${categoryDragX}px)`, transition: 'none' } : undefined}
          onTouchStart={handleFeedTouchStart}
          onTouchMove={handleFeedTouchMove}
          onTouchEnd={handleFeedTouchEnd}
          onTouchCancel={handleFeedTouchEnd}
        >
          {/* Book Again Section - personal, category-agnostic: All tab only */}
          {activeCategory === 'all' && bookAgain && bookAgain.length > 0 && (
            <section className="homepage-section">
              <h2 className="homepage-section-title">Book Again</h2>
              <div className="book-again-scroll">
                {/* Wrap in array to support multiple bookings in future */}
                {bookAgain.map((booking, index) => (
                  <div
                    key={booking._id || index}
                    className="book-again-card-new"
                    onClick={() => handleStylistClick(booking.salonId, booking.userId, null, booking.salonSlug)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="book-again-left">
                      <div className="book-again-avatar-container">
                        {booking.image ? (
                          <LazyImage
                            src={booking.image}
                            alt={booking.name}
                            wrapperClassName="book-again-avatar-new"
                            loading="eager"
                          />
                        ) : (
                          <div className="book-again-avatar-new placeholder">
                            <span>{(booking.name || '?')[0]}</span>
                          </div>
                        )}
                        {booking.reviewCount > 0 && (
                          <div className="book-again-rating-row">
                            <StarIcon filled={true} size={14} />
                            <span>{booking.averageRating?.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="book-again-center">
                      <h3 className="book-again-name-new">
                        <span className="book-again-stylist-name">{booking.name},</span>
                        {booking.salonName && <span className="book-again-salon-new"> {booking.salonName}</span>}
                      </h3>
                      <p className="book-again-service-new">{booking.lastService || 'Haircut'}</p>
                      <button
                        className="book-again-rebook-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStylistClick(booking.salonId, booking.userId, booking.lastServiceIds, booking.salonSlug);
                        }}
                      >
                        Rebook
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Loyalty nudge - the closest reward across the client's loyalty
              cards. Personal, so All tab only, like Book Again. */}
          {activeCategory === 'all' && loyaltyNudge && (
            <button
              type="button"
              className="loyalty-nudge"
              onClick={() => {
                hapticTick();
                handleSalonClick(loyaltyNudge.salonId, loyaltyNudge.salon?.slug);
              }}
            >
              <span className="loyalty-nudge-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M9 12.5l2 2 4-4.5" />
                </svg>
              </span>
              <span className="loyalty-nudge-text">{loyaltyNudgeText(loyaltyNudge)}</span>
              <span className="loyalty-nudge-chevron" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polyline points="9 18 15 12 9 6" /></svg>
              </span>
            </button>
          )}

          {/* Favourites Section - Full Width Cards with Overlay */}
          {fFavourites.length > 0 && (
            <section className="homepage-section">
              <h2 className="homepage-section-title">Favourites</h2>
              <div className="favourites-list">
                {fFavourites.slice(0, 2).map((favorite) => {
                  // Determine if this is a stylist or salon favourite
                  const isStylist = favorite.type === 'stylist';
                  // For stylists: use salon image as background, stylist image in circle
                  // For salons: use salon image as background, no circle
                  const backgroundImage = isStylist
                    ? (favorite.salonImage || favorite.backgroundImage || favorite.image)
                    : (favorite.image || favorite.salonImage || favorite.backgroundImage);
                  const avatarImage = isStylist ? favorite.image : null;
                  
                  return (
                    <div
                      key={favorite._id}
                      className={`favourite-card-full ${isStylist ? 'is-stylist' : 'is-salon'}`}
                      onClick={() => isStylist
                        ? handleStylistClick(favorite.salonId, favorite.userId, null, favorite.slug)
                        : handleSalonClick(favorite._id, favorite.slug)
                      }
                    >
                      {/* Full width background image */}
                      <div className="favourite-full-image">
                        {/* Swipeable gallery: galleryImages were already in the
                            payload but only one static photo rendered. The
                            carousel handles its own placeholder when empty. */}
                        <SalonImageCarousel
                          mainImage={backgroundImage}
                          galleryImages={favorite.galleryImages}
                          altText={favorite.name || favorite.salonName}
                          className="favourite-full-carousel"
                          primaryCategory={favorite.primaryCategory}
                        />
                        
                        {/* Heart: a real un-favourite toggle (was decorative).
                            Optimistic removal - the card leaves the list at
                            once; on API failure it comes back with a toast. */}
                        <button
                          type="button"
                          className="favourite-full-heart"
                          aria-label={`Remove ${favorite.name || favorite.salonName} from favourites`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnfavourite(favorite);
                          }}
                        >
                          <HeartIcon filled={true} size={20} />
                        </button>
                        
                        {/* Circular avatar - only show for stylists */}
                        {isStylist && (
                          <div className="favourite-full-avatar-wrap">
                            {avatarImage ? (
                              <LazyImage src={avatarImage} alt={favorite.name} className="favourite-full-avatar" wrapperClassName="favourite-full-avatar-wrapper circular" />
                            ) : (
                              <div className="favourite-full-avatar placeholder">
                                <span>{(favorite.name || '?')[0]}</span>
                              </div>
                            )}
                            {favorite.reviewCount > 0 && (
                              <div className="favourite-full-rating">
                                <StarIcon filled={true} size={14} />
                                <span>{favorite.averageRating?.toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Rating badge for salons (no avatar) - show in different position */}
                        {!isStylist && favorite.reviewCount > 0 && (
                          <div className="favourite-salon-rating-badge">
                            <StarIcon filled={true} size={14} />
                            <span>{favorite.averageRating?.toFixed(1)}</span>
                          </div>
                        )}
                        
                        {/* Text overlay at bottom */}
                        <div className="favourite-full-overlay">
                          <h3 className="favourite-full-name">{favorite.name || favorite.salonName}</h3>
                          <p className="favourite-full-salon">
                            {isStylist && favorite.salonName ? favorite.salonName : (favorite.administrativeAreaLevel2 || 'Local')}
                          </p>
                        </div>
                        
                        {/* Book button in bottom right */}
                        <button
                          className="favourite-full-book"
                          onClick={(e) => {
                            e.stopPropagation();
                            isStylist
                              ? handleStylistClick(favorite.salonId, favorite.userId, null, favorite.slug)
                              : handleSalonClick(favorite._id, favorite.slug);
                          }}
                        >
                          Book
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Recommended Stylists Section - Reusing Favourites card design */}
          {fRecommended.length > 0 && (
          <section className="homepage-section">
            <h2 className="homepage-section-title">Recommended Stylists</h2>
            <div className="favourites-list">
              {fRecommended.slice(0, 2).map((salon) => (
                <div
                  key={salon._id}
                  className="favourite-card-full"
                  onClick={() => salon.stylistId ? handleStylistClick(salon._id, salon.stylistId, null, salon.slug) : handleSalonClick(salon._id, salon.slug)}
                >
                  {/* Full width background image */}
                  <div className="favourite-full-image">
                    <SalonImageCarousel
                      mainImage={salon.image}
                      galleryImages={salon.galleryImages}
                      altText={salon.salonName}
                      className="favourite-full-carousel"
                      primaryCategory={salon.primaryCategory}
                    />
                    
                    {/* Circular avatar overlapping bottom left */}
                    <div className="favourite-full-avatar-wrap">
                      {salon.stylistImage ? (
                        <LazyImage src={salon.stylistImage} alt={salon.stylistName || salon.salonName} className="favourite-full-avatar" wrapperClassName="favourite-full-avatar-wrapper circular" />
                      ) : (
                        <div className="favourite-full-avatar placeholder">
                          <span>{(salon.stylistName || salon.salonName || '?')[0]}</span>
                        </div>
                      )}
                      {salon.reviewCount > 0 && (
                        <div className="favourite-full-rating">
                          <StarIcon filled={true} size={14} />
                          <span>{salon.averageRating?.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Text overlay at bottom */}
                    <div className="favourite-full-overlay">
                      <h3 className="favourite-full-name">{salon.stylistName || salon.salonName}</h3>
                      <p className="favourite-full-salon">{salon.salonName}</p>
                    </div>
                    
                    {/* Book button in bottom right */}
                    <button
                      className="favourite-full-book"
                      onClick={(e) => {
                        e.stopPropagation();
                        salon.stylistId ? handleStylistClick(salon._id, salon.stylistId, null, salon.slug) : handleSalonClick(salon._id, salon.slug);
                      }}
                    >
                      Book
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
          )}

          {/* AI-Powered Get the Look Section - HIDDEN
          <section className="homepage-section get-the-look-section-compact">
            <div className="get-the-look-header-compact">
              <div className="get-the-look-title-row">
                <h2 className="homepage-section-title">Get the Look</h2>
                <div className="ai-badge-small">
                  <span className="ai-sparkle">✨</span>
                  <span>AI</span>
                </div>
              </div>
            </div>
            
            <div className="looks-scroll">
              <div className="look-card-compact coming-soon-card">
                <div className="look-image-compact coming-soon-bg">
                  <span className="coming-soon-text">Coming Soon</span>
                </div>
                <span className="look-name-compact">New Feature</span>
              </div>
              
              <div className="look-card-compact">
                <div className="look-image-compact">
                  <img
                    src={require('../../../assets/images/professional-signup/hairdresser.png')}
                    alt="Balayage"
                  />
                  <span className="look-trending-badge">🔥</span>
                </div>
                <span className="look-name-compact">Balayage</span>
              </div>
              
              <div className="look-card-compact">
                <div className="look-image-compact">
                  <img
                    src={require('../../../assets/images/professional-signup/barber.png')}
                    alt="Modern Fade"
                  />
                </div>
                <span className="look-name-compact">Modern Fade</span>
              </div>
              
              <div className="look-card-compact">
                <div className="look-image-compact">
                  <img
                    src={require('../../../assets/images/salonpic.png')}
                    alt="Box Braids"
                  />
                </div>
                <span className="look-name-compact">Box Braids</span>
              </div>
              
              <div className="look-card-compact">
                <div className="look-image-compact">
                  <img
                    src={require('../../../assets/images/professional-signup/nail-tech.png')}
                    alt="Nail Art"
                  />
                </div>
                <span className="look-name-compact">Nail Art</span>
              </div>
              
              <div className="look-card-compact ai-finder-card">
                <div className="look-image-compact ai-finder-bg">
                  <div className="ai-finder-icon-small">✨</div>
                  <span className="ai-finder-text-small">Find Your Style</span>
                </div>
                <span className="look-name-compact">AI Style Finder</span>
              </div>
            </div>
          </section>
          */}

          {/* Featured Section - Always show with placeholder if no data */}
          <section className="homepage-section">
            <h2 className="homepage-section-title">Featured</h2>
            <div className="featured-list">
              {fFeatured.length > 0 ? (
                fFeatured.slice(0, 2).map((salon, index) => (
                  <div
                    key={salon._id}
                    className="featured-card"
                  >
                    <div className="featured-image-container">
                      <SalonImageCarousel
                        mainImage={salon.image}
                        galleryImages={salon.galleryImages}
                        altText={salon.salonName}
                        primaryCategory={salon.primaryCategory}
                        businessType={salon.businessType}
                        onClick={() => handleSalonClick(salon._id, salon.slug)}
                        overlay={
                          salon.reviewCount > 0 && (
                            <div className="featured-rating">
                              <StarIcon filled={true} size={11} />
                              <span>{salon.averageRating?.toFixed(1)}</span>
                              <span className="featured-review-count">({salon.reviewCount})</span>
                            </div>
                          )
                        }
                      />
                    </div>
                    <div className="featured-info" onClick={() => handleSalonClick(salon._id, salon.slug)}>
                      <div className="featured-info-left">
                        <h3 className="featured-name">
                          {salon.salonName}
                          {salon.isClaimed === false && <span className="unclaimed-badge">Unclaimed</span>}
                        </h3>
                        <p className="featured-location">
                          {salon.formattedAddress || salon.administrativeAreaLevel2}
                        </p>
                      </div>
                      <div className="featured-info-right">
                        <span className="featured-distance">{salon.distanceInMiles ? `${salon.distanceInMiles} mi` : ''}</span>
                        <span className="featured-type">{salon.primaryCategory || 'Hair Salon'}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                /* Placeholder cards when no data */
                [1, 2].map((index) => (
                  <div key={`featured-placeholder-${index}`} className="featured-card placeholder-card">
                    <div className="featured-image-container">
                      <PlaceholderImage className="featured-image" />
                      <div className="featured-dots">
                        {[0, 1, 2, 3, 4].map((dot) => (
                          <span key={dot} className={`featured-dot ${dot === 0 ? 'active' : ''}`} />
                        ))}
                      </div>
                    </div>
                    <div className="featured-info">
                      <div className="featured-info-left">
                        <h3 className="featured-name">Featured Salon</h3>
                        <p className="featured-location">
                          Coming soon...
                        </p>
                      </div>
                      <div className="featured-info-right">
                        <span className="featured-distance">-- mi</span>
                        <span className="featured-type">Hair Salon</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* New to Beu - fresh businesses; the API has always sent this
              list, the app just never rendered it. Same card as Near You. */}
          {fNewToBeu.length > 0 && (
          <section className="homepage-section">
            <h2 className="homepage-section-title">New to Beu</h2>
            <div className="near-you-scroll">
              {fNewToBeu.slice(0, 6).map((salon) => (
                <div
                  key={salon._id}
                  className="near-you-card"
                >
                  <div className="near-you-image-container">
                    <SalonImageCarousel
                      mainImage={salon.image}
                      galleryImages={salon.galleryImages}
                      altText={salon.salonName}
                      primaryCategory={salon.primaryCategory}
                      businessType={salon.businessType}
                      onClick={() => handleSalonClick(salon._id, salon.slug)}
                      singleImage
                      overlay={<div className="near-you-rating new-to-beu-badge">New</div>}
                    />
                  </div>
                  <div className="near-you-info" onClick={() => handleSalonClick(salon._id, salon.slug)}>
                    <h3 className="near-you-name">
                      {salon.salonName}
                      {salon.isClaimed === false && <span className="unclaimed-badge">Unclaimed</span>}
                    </h3>
                    <p className="near-you-location">{salon.formattedAddress || salon.administrativeAreaLevel2}</p>
                    <div className="near-you-meta">
                      <span className="near-you-distance">{salon.distanceInMiles ? `${salon.distanceInMiles} mi` : ''}</span>
                      <span className="near-you-type">{salon.primaryCategory || 'Hair Salon'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          )}

          {/* Recommended Near You Section - Always show with placeholder if no data */}

          <section className="homepage-section">
            <h2 className="homepage-section-title">Recommended Near You</h2>
            <div className="near-you-scroll">
              {fNearYou.length > 0 ? (
                fNearYou.slice(0, 4).map((salon) => (
                  <div
                    key={salon._id}
                    className="near-you-card"
                  >
                    <div className="near-you-image-container">
                      <SalonImageCarousel
                        mainImage={salon.image}
                        galleryImages={salon.galleryImages}
                        altText={salon.salonName}
                        primaryCategory={salon.primaryCategory}
                        businessType={salon.businessType}
                        onClick={() => handleSalonClick(salon._id, salon.slug)}
                        singleImage
                        overlay={
                          salon.reviewCount > 0 && (
                            <div className="near-you-rating">
                              <StarIcon filled={true} size={9} />
                              <span>{salon.averageRating?.toFixed(1)}</span>
                              <span>({salon.reviewCount})</span>
                            </div>
                          )
                        }
                      />
                    </div>
                    <div className="near-you-info" onClick={() => handleSalonClick(salon._id, salon.slug)}>
                      <h3 className="near-you-name">
                        {salon.salonName}
                        {salon.isClaimed === false && <span className="unclaimed-badge">Unclaimed</span>}
                      </h3>
                      <p className="near-you-location">{salon.formattedAddress || salon.administrativeAreaLevel2}</p>
                      <div className="near-you-meta">
                        <span className="near-you-distance">{salon.distanceInMiles ? `${salon.distanceInMiles} mi` : ''}</span>
                        <span className="near-you-type">{salon.primaryCategory || 'Hair Salon'}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                /* Placeholder cards when no data */
                [1, 2, 3].map((index) => (
                  <div key={`near-you-placeholder-${index}`} className="near-you-card placeholder-card">
                    <div className="near-you-image-container">
                      <PlaceholderImage className="near-you-image" />
                    </div>
                    <div className="near-you-info">
                      <h3 className="near-you-name">Recommended Salon</h3>
                      <p className="near-you-location">Coming soon...</p>
                      <div className="near-you-meta">
                        <span className="near-you-distance">-- mi</span>
                        <span className="near-you-type">Hair Salon</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Featured In Press Section - HIDDEN
          <section className="homepage-section featured-in-section">
            <h2 className="homepage-section-title">Featured In</h2>
            <div className="featured-in-scroll">
              <div className="featured-in-card">
                <div className="featured-in-logo">
                  <img src={require('../../../assets/images/web-img/bussiness1.png')} alt="Hairdressers Journal" />
                </div>
                <span className="featured-in-name">HJ</span>
              </div>
              <div className="featured-in-card">
                <div className="featured-in-logo">
                  <img src={require('../../../assets/images/web-img/bussiness2.png')} alt="Professional Beauty" />
                </div>
                <span className="featured-in-name">PB</span>
              </div>
              <div className="featured-in-card">
                <div className="featured-in-logo">
                  <img src={require('../../../assets/images/web-img/bussiness3.png')} alt="Featured Publication" />
                </div>
                <span className="featured-in-name">Press</span>
              </div>
              <div className="featured-in-card">
                <div className="featured-in-logo">
                  <img src={require('../../../assets/images/web-img/bussiness4.png')} alt="Featured Publication" />
                </div>
                <span className="featured-in-name">Media</span>
              </div>
            </div>
          </section>
          */}

          {/* Available Today Section - Reusing Favourites card design */}
          {fAvailableToday.length > 0 && (
          <section className="homepage-section">
            <h2 className="homepage-section-title">Available Today</h2>
            <div className="favourites-list">
              {fAvailableToday.slice(0, 2).map((salon) => (
                <div
                  key={salon._id}
                  className="favourite-card-full"
                  onClick={() => salon.stylistId ? handleStylistClick(salon._id, salon.stylistId, null, salon.slug) : handleSalonClick(salon._id, salon.slug)}
                >
                  {/* Full width background image */}
                  <div className="favourite-full-image">
                    <SalonImageCarousel
                      mainImage={salon.image}
                      galleryImages={salon.galleryImages}
                      altText={salon.salonName}
                      className="favourite-full-carousel"
                      primaryCategory={salon.primaryCategory}
                    />
                    
                    {/* Salon badge */}
                    <div className="favourite-full-badge">Salon</div>
                    
                    {/* Circular avatar overlapping bottom left */}
                    <div className="favourite-full-avatar-wrap">
                      {salon.stylistImage ? (
                        <LazyImage src={salon.stylistImage} alt={salon.stylistName || salon.salonName} className="favourite-full-avatar" wrapperClassName="favourite-full-avatar-wrapper circular" />
                      ) : (
                        <div className="favourite-full-avatar placeholder">
                          <span>{(salon.stylistName || salon.salonName || '?')[0]}</span>
                        </div>
                      )}
                      {salon.reviewCount > 0 && (
                        <div className="favourite-full-rating">
                          <StarIcon filled={true} size={14} />
                          <span>{salon.averageRating?.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Text overlay at bottom */}
                    <div className="favourite-full-overlay">
                      <h3 className="favourite-full-name">{salon.stylistName || salon.salonName}</h3>
                      <p className="favourite-full-salon">{salon.salonName}</p>
                    </div>
                    
                    {/* Book button in bottom right */}
                    <button
                      className="favourite-full-book"
                      onClick={(e) => {
                        e.stopPropagation();
                        salon.stylistId ? handleStylistClick(salon._id, salon.stylistId, null, salon.slug) : handleSalonClick(salon._id, salon.slug);
                      }}
                    >
                      Book
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
          )}

          {/* Discover More Salons Section - Only show if there are salons or still loading */}
          {(fDiscoverSalons.length > 0 || isLoadingMoreSalons) && (
            <section className="homepage-section discover-more-section">
              <h2 className="homepage-section-title">Discover More Salons</h2>
              <div className="discover-more-grid">
                {isLoadingMoreSalons && discoverMoreSalons.length === 0 ? (
                  [1, 2, 3, 4].map((i) => (
                    <div key={`skeleton-salon-${i}`} className="discover-more-card skeleton-card">
                      <div className="discover-more-image-container skeleton-image"></div>
                      <div className="discover-more-info">
                        <div className="skeleton-text skeleton-title"></div>
                        <div className="skeleton-text skeleton-subtitle"></div>
                      </div>
                    </div>
                  ))
                ) : (
                  fDiscoverSalons.map((salon) => (
                    <div
                      key={salon._id}
                      className="discover-more-card"
                    >
                      <div className="discover-more-image-container">
                        <SalonImageCarousel
                          mainImage={salon.image}
                          galleryImages={salon.galleryImages}
                          altText={salon.salonName}
                          primaryCategory={salon.primaryCategory}
                          businessType={salon.businessType}
                          onClick={() => handleSalonClick(salon._id, salon.slug)}
                          overlay={
                            salon.reviewCount > 0 && (
                              <div className="discover-more-rating">
                                <StarIcon filled={true} size={9} />
                                <span>{salon.averageRating?.toFixed(1)}</span>
                                <span>({salon.reviewCount})</span>
                              </div>
                            )
                          }
                        />
                      </div>
                      <div className="discover-more-info" onClick={() => handleSalonClick(salon._id, salon.slug)}>
                        <h3 className="discover-more-name">
                          {salon.salonName}
                          {salon.isClaimed === false && <span className="unclaimed-badge">Unclaimed</span>}
                        </h3>
                        <p className="discover-more-location">{salon.formattedAddress || salon.administrativeAreaLevel2}</p>
                        <div className="discover-more-meta">
                          <span className="discover-more-distance">{salon.distanceInMiles ? `${salon.distanceInMiles} mi` : ''}</span>
                          <span className="discover-more-type">{salon.primaryCategory || 'Hair Salon'}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {hasMoreSalons && discoverMoreSalons.length > 0 && (
                <button
                  className="discover-more-btn"
                  onClick={handleLoadMoreSalons}
                  disabled={isLoadingMoreSalons}
                >
                  {isLoadingMoreSalons ? 'Loading...' : 'View More'}
                </button>
              )}
            </section>
          )}

          {/* Discover More Stylists Section - Only show if there are stylists or still loading */}
          {(fDiscoverStylists.length > 0 || isLoadingMoreStylists) && (
            <section className="homepage-section discover-more-section">
              <h2 className="homepage-section-title">Discover More Stylists</h2>
              <div className="discover-more-grid">
                {isLoadingMoreStylists && discoverMoreStylists.length === 0 ? (
                  [1, 2, 3, 4].map((i) => (
                    <div key={`skeleton-stylist-${i}`} className="discover-more-card skeleton-card">
                      <div className="discover-more-image-container skeleton-image"></div>
                      <div className="discover-more-info">
                        <div className="skeleton-text skeleton-title"></div>
                        <div className="skeleton-text skeleton-subtitle"></div>
                      </div>
                    </div>
                  ))
                ) : (
                  fDiscoverStylists.map((stylist) => (
                    <div
                      key={stylist._id + '-' + (stylist.stylistId || '')}
                      className="discover-more-card"
                    >
                      <div className="discover-more-image-container">
                        <SalonImageCarousel
                          mainImage={stylist.stylistImage || stylist.image}
                          galleryImages={stylist.galleryImages}
                          altText={stylist.stylistName || stylist.salonName}
                          primaryCategory={stylist.primaryCategory}
                          businessType={stylist.businessType}
                          onClick={() => stylist.stylistId ? handleStylistClick(stylist._id, stylist.stylistId, null, stylist.slug) : handleSalonClick(stylist._id, stylist.slug)}
                          overlay={
                            stylist.reviewCount > 0 && (
                              <div className="discover-more-rating">
                                <StarIcon filled={true} size={9} />
                                <span>{stylist.averageRating?.toFixed(1)}</span>
                                <span>({stylist.reviewCount})</span>
                              </div>
                            )
                          }
                        />
                      </div>
                      <div className="discover-more-info" onClick={() => stylist.stylistId ? handleStylistClick(stylist._id, stylist.stylistId, null, stylist.slug) : handleSalonClick(stylist._id, stylist.slug)}>
                        <h3 className="discover-more-name">{stylist.stylistName || stylist.salonName}</h3>
                        <p className="discover-more-location">{stylist.salonName}</p>
                        <div className="discover-more-meta">
                          <span className="discover-more-distance">{stylist.distanceInMiles ? `${stylist.distanceInMiles} mi` : ''}</span>
                          <span className="discover-more-type">{stylist.primaryCategory || 'Stylist'}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {hasMoreStylists && discoverMoreStylists.length > 0 && (
                <button
                  className="discover-more-btn"
                  onClick={handleLoadMoreStylists}
                  disabled={isLoadingMoreStylists}
                >
                  {isLoadingMoreStylists ? 'Loading...' : 'View More'}
                </button>
              )}
            </section>
          )}

          {/* The Top-Rated Destination Section - HIDDEN
          <section className="homepage-section top-rated-section">
            <h2 className="top-rated-title">The UK's top-rated destination for beauty & wellness</h2>
            <p className="top-rated-subtitle">One platform, endless possibilities. Trusted by the best in the industry</p>
            
            <div className="big-stats-grid">
              <div className="big-stat-item">
                <span className="big-stat-number gradient-text">500K+</span>
                <span className="big-stat-label">appointments booked</span>
              </div>
              <div className="big-stat-item">
                <span className="big-stat-number gradient-text">50+</span>
                <span className="big-stat-label">cities across the UK</span>
              </div>
              <div className="big-stat-item">
                <span className="big-stat-number gradient-text">2,500+</span>
                <span className="big-stat-label">partner businesses</span>
              </div>
              <div className="big-stat-item">
                <span className="big-stat-number gradient-text">15,000+</span>
                <span className="big-stat-label">stylists and professionals</span>
              </div>
            </div>
          </section>
          */}

          {/* Reviews Section - HIDDEN
          <section className="homepage-section reviews-section">
            <h2 className="homepage-section-title">Reviews</h2>
            <div className="premium-testimonials">
              <div className="premium-testimonial-card">
                <h3 className="testimonial-headline">Best booking system</h3>
                <p className="testimonial-text">
                  Great experience, easy to book. Paying for treatments is so convenient. No cash or cards needed!
                </p>
                <div className="testimonial-author-premium">
                  <img
                    src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face"
                    alt="Lucy"
                    className="testimonial-photo"
                  />
                  <div className="testimonial-author-info">
                    <span className="testimonial-name">Lucy</span>
                    <span className="testimonial-location">London, UK</span>
                  </div>
                </div>
              </div>
              
              <div className="premium-testimonial-card">
                <h3 className="testimonial-headline">Found amazing barbers</h3>
                <p className="testimonial-text">
                  Beu's reminders make life so much easier. I also found a few good barbershops that I didn't know existed.
                </p>
                <div className="testimonial-author-premium">
                  <img
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"
                    alt="Dan"
                    className="testimonial-photo"
                  />
                  <div className="testimonial-author-info">
                    <span className="testimonial-name">Dan</span>
                    <span className="testimonial-location">Manchester, UK</span>
                  </div>
                </div>
              </div>
              
              <div className="premium-testimonial-card">
                <h3 className="testimonial-headline">Best platform for barbers</h3>
                <p className="testimonial-text">
                  I've been using Beu for 2 years and it's by far the best booking platform I've used. Highly recommend it!
                </p>
                <div className="testimonial-author-premium">
                  <img
                    src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
                    alt="Dale"
                    className="testimonial-photo"
                  />
                  <div className="testimonial-author-info">
                    <span className="testimonial-name">Dale</span>
                    <span className="testimonial-location">Birmingham, UK</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
          */}

          {/* Download App Section - HIDDEN
          <section className="homepage-section download-app-section">
            <div className="download-app-content">
              <div className="download-app-text">
                <h2 className="download-app-title">Download the Beu app</h2>
                <p className="download-app-description">
                  Book unforgettable beauty and wellness experiences with the Beu mobile app
                </p>
                <div className="app-store-buttons">
                  <a href="https://apps.apple.com/app/beu" target="_blank" rel="noopener noreferrer" className="app-store-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div className="app-store-text">
                      <span className="app-store-label">Download on the</span>
                      <span className="app-store-name">App Store</span>
                    </div>
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.beu" target="_blank" rel="noopener noreferrer" className="app-store-btn google-play">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    <div className="app-store-text">
                      <span className="app-store-label">Get it on</span>
                      <span className="app-store-name">Google Play</span>
                    </div>
                  </a>
                </div>
              </div>
              <div className="download-app-mockups">
                <div className="phone-mockup">
                  <img
                    src={require('../../../assets/images/web-img/Beu-Marketplace1.png')}
                    alt="Beu App"
                    className="phone-screen"
                  />
                </div>
              </div>
            </div>
          </section>
          */}

          {/* Beu for Business Section - HIDDEN
          <section className="homepage-section beu-for-biz-section">
            <div className="beu-for-biz-content">
              <div className="beu-for-biz-text">
                <h2 className="beu-for-biz-title">Beu for business</h2>
                <p className="beu-for-biz-description">
                  The all-in-one platform that empowers salons, solo professionals, and teams with employed or self-employed staff. Built for how you work.
                </p>
                <ul className="beu-for-biz-features">
                  <li>
                    <span className="feature-check">✓</span>
                    <span className="feature-content">Payment splitting & commission management</span>
                  </li>
                  <li>
                    <span className="feature-check">✓</span>
                    <span className="feature-content">Online shop & gift vouchers</span>
                  </li>
                  <li>
                    <span className="feature-check">✓</span>
                    <span className="feature-content">Team rotas & individual calendars</span>
                  </li>
                  <li>
                    <span className="feature-check">✓</span>
                    <span className="feature-content">Online booking with deposits</span>
                  </li>
                  <li>
                    <span className="feature-check">✓</span>
                    <span className="feature-content">Automated reminders & marketing</span>
                  </li>
                  <li>
                    <span className="feature-check">✓</span>
                    <span className="feature-content">Perfect for solos & teams</span>
                  </li>
                </ul>
                <div className="beu-for-biz-buttons">
                  <button
                    className="beu-for-biz-primary-btn"
                    onClick={() => history.push('/signup/professional')}
                  >
                    Get Started Free
                  </button>
                  <button
                    className="beu-for-biz-secondary-btn"
                    onClick={() => history.push('/get-software')}
                  >
                    Learn More
                  </button>
                </div>
              </div>
              <div className="beu-for-biz-image">
                <img
                  src={require('../../../assets/images/web-img/BusinessManagement.png')}
                  alt="Beu Business Management"
                  className="biz-preview-image"
                />
              </div>
            </div>
          </section>
          */}

          {/* Browse by Category - SEO internal linking */}
          <section className="homepage-section browse-by-category-section">
            <h2 className="homepage-section-title">Browse by Category</h2>
            <div className="browse-category-grid">
              <div className="browse-category-column">
                <h3 className="browse-category-heading">Hair Salons</h3>
                <Link to="/hair-salons/in/london" className="browse-category-link">Hair Salons in London</Link>
                <Link to="/hair-salons/in/manchester" className="browse-category-link">Hair Salons in Manchester</Link>
                <Link to="/hair-salons/in/birmingham" className="browse-category-link">Hair Salons in Birmingham</Link>
                <Link to="/hair-salons/in/leeds" className="browse-category-link">Hair Salons in Leeds</Link>
                <Link to="/hair-salons/in/glasgow" className="browse-category-link">Hair Salons in Glasgow</Link>
              </div>
              <div className="browse-category-column">
                <h3 className="browse-category-heading">Barbers</h3>
                <Link to="/barbers/in/london" className="browse-category-link">Barbers in London</Link>
                <Link to="/barbers/in/manchester" className="browse-category-link">Barbers in Manchester</Link>
                <Link to="/barbers/in/birmingham" className="browse-category-link">Barbers in Birmingham</Link>
                <Link to="/barbers/in/leeds" className="browse-category-link">Barbers in Leeds</Link>
                <Link to="/barbers/in/glasgow" className="browse-category-link">Barbers in Glasgow</Link>
              </div>
              <div className="browse-category-column">
                <h3 className="browse-category-heading">Beauty & Nails</h3>
                <Link to="/beauty-salons/in/london" className="browse-category-link">Beauty Salons in London</Link>
                <Link to="/nail-salons/in/london" className="browse-category-link">Nail Salons in London</Link>
                <Link to="/beauty-salons/in/manchester" className="browse-category-link">Beauty Salons in Manchester</Link>
                <Link to="/nail-salons/in/manchester" className="browse-category-link">Nail Salons in Manchester</Link>
                <Link to="/spas/in/london" className="browse-category-link">Spas in London</Link>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
    </>
  );

  // If logged in, use the dashboard layout with sidebar
  if (isLoggedIn) {
    return (
      <div className="client-dashboard homepage-dashboard">
        {/* Sidebar Navigation - Desktop only */}
        <DashboardSidebar
          isMobileOpen={menuOpen}
          onCloseMobile={closeMenu}
        />

        {/* Main Content Area */}
        <div className="client-dashboard-content">
          {/* Main Content */}
          <main className="dashboard-main homepage-dashboard-main">
            {renderMainContent()}
          </main>
        </div>
      </div>
    );
  }

  // If not logged in, use the standalone layout
  return (
    <div className="new-homepage logged-out">
      <div className="homepage-content-new">
        {renderMainContent()}
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  const { hairdresser, salon, user, salonList, setting, services, createBooking } = state;
  return {
    hairdresser,
    salon,
    user,
    setting,
    salonList,
    services,
    createBooking,
  };
}

export default connect(mapStateToProps)(NewHomePage);
